import { useEffect, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { api, getUser, setUser, setToken, logout, ROLE_LABEL } from "../lib/api";
import { Trash2, Truck, Trash, MapPin, X as XIcon, Lock, CheckCircle2, Gavel, MessageSquare, Recycle } from "lucide-react";
import { toast } from "sonner";
import CarrierLicenceCard from "../components/CarrierLicenceCard";
import CarrierComplianceCard from "../components/CarrierComplianceCard";
import CarrierKycCard from "../components/CarrierKycCard";
import CarrierProfileCard from "../components/CarrierProfileCard";
import ServiceAreaCard from "../components/ServiceAreaCard";
import CarrierUpgradeBanner from "../components/CarrierUpgradeBanner";
import StripeConnectCard from "../components/StripeConnectCard";
import { ensurePushRegistration } from "../lib/push";

export default function Dashboard() {
  const navigate = useNavigate();

  // OAuth callback: if Emergent redirected us here with #session_id=…, exchange
  // it BEFORE the auth gate kicks in. useRef guards against StrictMode double
  // invocation. See /app/auth_testing.md for the full flow.
  const oauthHandled = useRef(false);
  const hash = typeof window !== "undefined" ? window.location.hash : "";
  const incomingSessionId = hash.includes("session_id=")
    ? new URLSearchParams(hash.slice(1)).get("session_id")
    : null;

  const [oauthExchanging, setOauthExchanging] = useState(Boolean(incomingSessionId));
  let user = getUser();
  const [posts, setPosts] = useState([]);
  const [carrierBids, setCarrierBids] = useState([]);
  const [escrow, setEscrow] = useState(null);
  const [convos, setConvos] = useState([]);
  const [myReuse, setMyReuse] = useState([]);

  useEffect(() => {
    if (!incomingSessionId || oauthHandled.current) return;
    oauthHandled.current = true;
    (async () => {
      try {
        const { data } = await api.post("/auth/google-callback", { session_id: incomingSessionId });
        setToken(data.token);
        setUser(data.user);
        // Strip the fragment so we don't re-process on refresh.
        window.history.replaceState({}, "", window.location.pathname);
        toast.success(`Welcome${data.user?.name ? `, ${data.user.name}` : ""}!`);
        if (data.user?.needs_role_selection) {
          // Fresh OAuth signup — send them to pick their role.
          navigate("/onboarding/role", { replace: true });
          return;
        }
        // Force a full state refresh now that we have a user.
        window.location.reload();
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Google sign-in failed");
        window.history.replaceState({}, "", window.location.pathname);
        setOauthExchanging(false);
        navigate("/login");
      }
    })();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (oauthExchanging) return;   // wait for OAuth exchange to finish
    if (!user) {
      navigate("/login");
      return;
    }
    // OAuth signups land here provisionally — send them to pick a role first.
    if (user.needs_role_selection) {
      navigate("/onboarding/role", { replace: true });
      return;
    }
    if (user.role === "admin") {
      navigate("/admin");
      return;
    }
    // Validate session — if the stored user no longer exists server-side
    // (e.g. after a reseed), clear localStorage and bounce to /login.
    (async () => {
      try {
        const { data: fresh } = await api.get(`/auth/me/${user.id}`);
        setUser(fresh);
        refresh();
        // Native push registration is no-op on web; safe to call here.
        ensurePushRegistration({ navigate });
      } catch (err) {
        if (err?.response?.status === 404 || err?.response?.status === 403) {
          logout();
          toast.error("Session expired — please sign in again");
          navigate("/login");
        } else {
          // network or other transient — still try to render with cached data
          refresh();
        }
      }
    })();
    // eslint-disable-next-line
  }, []);

  const refresh = async () => {
    try {
      if (user.role === "carrier") {
        const { data } = await api.get(`/bids/carrier/${user.id}`, { params: { user_id: user.id } });
        setCarrierBids(data);
        try {
          const er = await api.get(`/escrow/carrier/${user.id}`);
          setEscrow(er.data);
        } catch {
          setEscrow(null);
        }
      } else {
        const { data: p } = await api.get(`/waste-posts`, { params: { user_id: user.id } });
        setPosts(p);
      }
    } catch (err) {
      console.warn("Dashboard refresh failed", err?.response?.status);
    }
    // Phase 3 — fetch reuse listings owned by this user (all statuses).
    try {
      const { data: r } = await api.get(`/reuse-listings`, { params: { owner_id: user.id } });
      setMyReuse(r);
    } catch {
      setMyReuse([]);
    }
    try {
      const { data: cs } = await api.get(`/conversations/${user.id}`);
      setConvos(cs);
    } catch {
      setConvos([]);
    }
  };

  const cancelPost = async (id) => {
    if (!window.confirm("Cancel this job? Refunds only apply if cancelled ≥24h before the pickup time.")) return;
    try {
      const { data } = await api.post(`/waste-posts/${id}/cancel`, null, { params: { user_id: user.id } });
      toast.success(data.refund_eligible ? "Cancelled — refund will be processed" : "Cancelled (within 24h — no refund)");
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const deletePost = async (id) => {
    await api.delete(`/waste-posts/${id}`, { params: { user_id: user.id } });
    toast.success("Post removed");
    refresh();
  };

  if (oauthExchanging) {
    return (
      <Layout>
        <div data-testid="oauth-exchanging" className="max-w-md mx-auto px-5 sm:px-8 py-24 text-center text-sm text-[#6B6965]">
          Finishing sign-in…
        </div>
      </Layout>
    );
  }

  if (!user) return null;

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-10">
        <div className="fade-up">
          <Badge className="bg-[#EBE8E0] text-[#1B3624] hover:bg-[#EBE8E0] uppercase tracking-wide text-[10px]">
            {ROLE_LABEL[user.role]} dashboard
          </Badge>
          <h1
            className="mt-3 text-3xl sm:text-4xl lg:text-5xl tracking-tighter font-medium"
            style={{ fontFamily: "Outfit" }}
            data-testid="dashboard-title"
          >
            Hi {user.name.split(" ")[0]}, choose your route.
          </h1>
          {user.role !== "carrier" && (
            <p className="mt-2 text-sm sm:text-base text-[#6B6965] max-w-xl">
              Reuse it free, remove a one-off item, or clear a whole space — pick the right route below.
            </p>
          )}
        </div>

        {/* PRIMARY: 3-route UI (Reuse / Remove / Clear). Only for non-carriers. */}
        {user.role !== "carrier" && (
          <div data-testid="dashboard-routes" className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Card
              role="button"
              tabIndex={0}
              data-testid="route-reuse"
              onClick={() => navigate("/reuse")}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); navigate("/reuse"); } }}
              className="p-5 rounded-2xl border-[#E2DFD8] bg-white hover:border-[#4A6B56] transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#4A6B56]"
            >
              <Recycle className="w-6 h-6 text-[#4A6B56]" />
              <div className="mt-3 font-medium" style={{ fontFamily: "Outfit" }}>Reuse</div>
              <div className="text-xs text-[#6B6965] mt-1">Give it away, free. A neighbour collects.</div>
            </Card>
            <Card
              role="button"
              tabIndex={0}
              data-testid="route-remove"
              onClick={() => navigate("/post-waste")}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); navigate("/post-waste"); } }}
              className="p-5 rounded-2xl border-[#E2DFD8] bg-white hover:border-[#4A6B56] transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#4A6B56]"
            >
              <Trash2 className="w-6 h-6 text-[#D67B54]" />
              <div className="mt-3 font-medium" style={{ fontFamily: "Outfit" }}>Remove</div>
              <div className="text-xs text-[#6B6965] mt-1">One-off item. Licensed carrier collects.</div>
            </Card>
            <Card
              role="button"
              tabIndex={0}
              data-testid="route-clear"
              onClick={() => navigate("/post-waste")}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); navigate("/post-waste"); } }}
              className="p-5 rounded-2xl border-[#E2DFD8] bg-white hover:border-[#4A6B56] transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#4A6B56]"
            >
              <Truck className="w-6 h-6 text-[#1B3624]" />
              <div className="mt-3 font-medium" style={{ fontFamily: "Outfit" }}>Clear</div>
              <div className="text-xs text-[#6B6965] mt-1">Whole room, garden or tenancy — one pro job.</div>
            </Card>
          </div>
        )}

        {/* Carrier CTA row (unchanged priority for carriers) */}
        {user.role === "carrier" && (
          <div className="mt-6 flex flex-wrap gap-2">
            <Button
              data-testid="action-browse-waste"
              onClick={() => navigate("/marketplace")}
              className="rounded-full bg-[#D67B54] hover:bg-[#c66c44] text-white"
            >
              <Truck className="w-4 h-4 mr-1" /> Browse leads
            </Button>
          </div>
        )}

        {/* Carrier licence verification */}
        {user.role === "carrier" && (
          <div className="mt-8 grid md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <CarrierProfileCard onChanged={refresh} />
            </div>
            <CarrierLicenceCard onChanged={refresh} />
            <ServiceAreaCard user={user} onChange={refresh} />
            <CarrierComplianceCard onChanged={refresh} />
            <CarrierKycCard onChanged={refresh} />
            <div className="md:col-span-2">
              <StripeConnectCard onChanged={refresh} />
            </div>
          </div>
        )}

        {/* Customer -> carrier upgrade prompt */}
        {(user.role === "homeowner" || user.role === "business") && (
          <div className="mt-8">
            <CarrierUpgradeBanner user={user} onChange={refresh} />
          </div>
        )}

        {/* Inbox card — surfaces unread message count to drive daily engagement */}
        {convos.length > 0 && (() => {
          const totalUnread = convos.reduce((a, c) => a + (c.unread_count || 0), 0);
          const recent = convos.slice(0, 3);
          return (
            <Card data-testid="dashboard-inbox-card" className={`mt-8 rounded-2xl p-5 border ${totalUnread > 0 ? "border-[#D67B54]/40 bg-[#FDF2EB]" : "border-[#E2DFD8] bg-[#F6F4F0]"}`}>
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-3">
                  <div className={`w-11 h-11 rounded-2xl flex items-center justify-center ${totalUnread > 0 ? "bg-[#D67B54] text-white" : "bg-[#1B3624] text-white"}`}>
                    <MessageSquare className="w-5 h-5" />
                  </div>
                  <div>
                    <div className="flex items-baseline gap-2">
                      <span data-testid="dashboard-inbox-title" className="text-lg font-medium text-[#1B3624]" style={{ fontFamily: "Outfit" }}>
                        {totalUnread > 0 ? `${totalUnread} new ${totalUnread === 1 ? "message" : "messages"}` : "Inbox"}
                      </span>
                      {totalUnread > 0 && (
                        <Badge data-testid="dashboard-unread-badge" className="bg-[#D67B54] text-white">{totalUnread}</Badge>
                      )}
                    </div>
                    <div className="text-xs text-[#6B6965] mt-0.5">{convos.length} conversation{convos.length === 1 ? "" : "s"}</div>
                  </div>
                </div>
              </div>
              <div className="mt-4 grid sm:grid-cols-3 gap-2">
                {recent.map((c) => (
                  <button
                    key={c.conversation_id}
                    data-testid={`inbox-convo-${c.peer_id}`}
                    onClick={() => {
                      if (c.waste_post_id) navigate(`/waste/${c.waste_post_id}`);
                    }}
                    className="text-left bg-white border border-[#E2DFD8] rounded-2xl p-3 hover:border-[#1B3624] transition min-w-0 overflow-hidden"
                  >
                    <div className="flex items-center justify-between gap-1 min-w-0">
                      <span className="font-medium text-sm text-[#1B3624] truncate min-w-0">{c.peer_name}</span>
                      {c.unread_count > 0 && (
                        <Badge className="bg-[#D67B54] text-white text-[10px] h-5 px-1.5 shrink-0">{c.unread_count}</Badge>
                      )}
                    </div>
                    <div className={`text-xs truncate mt-1 min-w-0 ${c.unread_count > 0 ? "text-[#1A1A1A]" : "text-[#6B6965]"}`}>
                      {c.last_flagged && "⚠️ "}{c.last_body}
                    </div>
                  </button>
                ))}
              </div>
            </Card>
          );
        })()}

        {/* Stat bento */}
        <div className="mt-8 grid md:grid-cols-12 gap-4">
          <StatCard
            className="md:col-span-6"
            icon={<Trash className="w-5 h-5" />}
            label={user.role === "carrier" ? "Active bids" : "Open removal posts"}
            value={user.role === "carrier" ? carrierBids.filter((b) => b.status === "pending").length : posts.filter((p) => p.status === "open").length}
            testId="stat-1"
          />
          <StatCard
            className="md:col-span-6"
            icon={<Truck className="w-5 h-5" />}
            label={user.role === "carrier" ? "Won contracts" : "Accepted jobs"}
            value={user.role === "carrier" ? carrierBids.filter((b) => b.status === "accepted").length : posts.filter((p) => p.status === "accepted").length}
            testId="stat-2"
          />
        </div>

        {/* Carrier earnings (escrow) */}
        {user.role === "carrier" && escrow && (escrow.held > 0 || escrow.released > 0 || escrow.disputed > 0) && (
          <Section title="Your earnings" testId="section-earnings">
            <div className="grid sm:grid-cols-3 gap-4">
              <Card data-testid="earnings-held" className="p-5 rounded-2xl border-[#E2DFD8] bg-[#F6F4F0]">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center text-[#D67B54]">
                    <Lock className="w-5 h-5" />
                  </div>
                  <div>
                    <div className="text-xs text-[#6B6965] uppercase tracking-wide">In escrow</div>
                    <div className="text-2xl font-medium mt-0.5" style={{ fontFamily: "Outfit" }}>£{escrow.held.toFixed(2)}</div>
                  </div>
                </div>
                <div className="text-[10px] text-[#6B6965] mt-2">Clears 24h after job completion (unless disputed).</div>
              </Card>
              <Card data-testid="earnings-released" className="p-5 rounded-2xl border-[#E2DFD8] bg-[#1B3624] text-white">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center text-white">
                    <CheckCircle2 className="w-5 h-5" />
                  </div>
                  <div>
                    <div className="text-xs text-white/60 uppercase tracking-wide">Released</div>
                    <div className="text-2xl font-medium mt-0.5" style={{ fontFamily: "Outfit" }}>£{escrow.released.toFixed(2)}</div>
                  </div>
                </div>
                <div className="text-[10px] text-white/60 mt-2">Cleared and yours.</div>
              </Card>
              <Card data-testid="earnings-disputed" className="p-5 rounded-2xl border-red-200 bg-red-50/60">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center text-red-700">
                    <Gavel className="w-5 h-5" />
                  </div>
                  <div>
                    <div className="text-xs text-red-700 uppercase tracking-wide">In dispute</div>
                    <div className="text-2xl font-medium mt-0.5" style={{ fontFamily: "Outfit" }}>£{escrow.disputed.toFixed(2)}</div>
                  </div>
                </div>
                <div className="text-[10px] text-red-700/80 mt-2">Pending admin review.</div>
              </Card>
            </div>
            {escrow.items.length > 0 && (
              <div className="mt-4 space-y-2">
                {escrow.items.slice(0, 6).map((it) => (
                  <Link key={it.session_id} to={`/waste/${it.post_id}`} data-testid={`earnings-row-${it.session_id}`} className="block">
                    <Card className="p-4 rounded-2xl border-[#E2DFD8] bg-white hover:bg-[#F6F4F0] transition flex items-center justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="font-medium truncate" style={{ fontFamily: "Outfit" }}>{it.post_title || "—"}</div>
                        <div className="text-xs text-[#6B6965] mt-0.5">
                          {it.escrow_status === "held" && it.escrow_release_at && `Releases ${new Date(it.escrow_release_at).toLocaleString("en-GB", { dateStyle: "short", timeStyle: "short" })}`}
                          {it.escrow_status === "released" && it.escrow_released_at && `Released ${new Date(it.escrow_released_at).toLocaleString("en-GB", { dateStyle: "short", timeStyle: "short" })}`}
                          {it.escrow_status === "disputed" && "Awaiting admin review"}
                          {it.escrow_status === "refunded" && "Refunded to customer"}
                        </div>
                      </div>
                      <EscrowPill status={it.escrow_status} />
                      <div className="text-right">
                        <div className="text-lg font-medium" style={{ fontFamily: "Outfit" }}>£{Number(it.carrier_take || 0).toFixed(2)}</div>
                      </div>
                    </Card>
                  </Link>
                ))}
              </div>
            )}
          </Section>
        )}

        {/* Carrier bids */}
        {user.role === "carrier" && (
          <Section title="Marketplace leads you've bid on" testId="section-bids">
            {carrierBids.length === 0 ? (
              <Empty text="No leads picked up yet. Browse the waste marketplace to start." />
            ) : (
              <div className="grid md:grid-cols-2 gap-4">
                {carrierBids.map((b) => (
                  <Card key={b.id} data-testid={`bid-${b.id}`} className="p-5 rounded-2xl border-[#E2DFD8] bg-[#F6F4F0]">
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="text-xs text-[#6B6965] uppercase tracking-wide">Bid</div>
                        <div className="text-2xl mt-1 font-medium" style={{ fontFamily: "Outfit" }}>
                          £{b.amount.toFixed(2)}
                        </div>
                        <div className="text-sm text-[#6B6965] mt-2">{b.message}</div>
                      </div>
                      <BidStatus status={b.status} />
                    </div>
                    <Link to={`/waste/${b.waste_post_id}`} className="mt-3 inline-block text-sm font-medium text-[#4A6B56] hover:underline">
                      View post →
                    </Link>
                  </Card>
                ))}
              </div>
            )}
          </Section>
        )}

        {/* Homeowner/business waste posts */}
        {user.role !== "carrier" && (
          <Section title="Your removal jobs" testId="section-posts">
            {posts.length === 0 ? (
              <Empty text="You haven't posted a removal job yet." cta="Post your first" to="/post-waste" />
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {posts.map((p) => (
                  <Card key={p.id} data-testid={`post-${p.id}`} className="overflow-hidden rounded-2xl border-[#E2DFD8] p-0">
                    {p.image_url && <img src={p.image_url} alt="" className="w-full h-36 object-cover" />}
                    <div className="p-5">
                      <div className="flex items-center justify-between">
                        <Badge className="bg-[#EBE8E0] text-[#1B3624] hover:bg-[#EBE8E0] uppercase text-[10px]">
                          {p.waste_type}
                        </Badge>
                        <PostStatus status={p.status} />
                      </div>
                      <div className="mt-2 font-medium" style={{ fontFamily: "Outfit" }}>{p.title}</div>
                      <div className="text-xs text-[#6B6965] mt-1 flex items-center gap-1">
                        <MapPin className="w-3 h-3" /> {p.address}
                      </div>
                      <div className="mt-4 flex items-center justify-between gap-2">
                        <Link to={`/waste/${p.id}`} className="text-sm font-medium text-[#4A6B56] hover:underline">
                          View bids →
                        </Link>
                        <div className="flex gap-1">
                          {(p.status === "open" || p.status === "accepted") && (
                            <Button
                              data-testid={`cancel-post-${p.id}`}
                              variant="ghost"
                              size="icon"
                              onClick={() => cancelPost(p.id)}
                              title="Cancel job"
                              className="text-[#6B6965] hover:text-[#D67B54]"
                            >
                              <XIcon className="w-4 h-4" />
                            </Button>
                          )}
                          <Button
                            data-testid={`delete-post-${p.id}`}
                            variant="ghost"
                            size="icon"
                            onClick={() => deletePost(p.id)}
                            className="text-[#6B6965] hover:text-red-600"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </Section>
        )}

        {/* Phase 3 — user's reuse listings + request counts */}
        {user.role !== "carrier" && (
          <Section title="Your Reuse listings" testId="section-reuse">
            {myReuse.length === 0 ? (
              <Empty text="Nothing listed for reuse yet." cta="List an item" to="/reuse/new" />
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {myReuse.map((l) => (
                  <ReuseTile key={l.id} listing={l} onDelete={async (id) => {
                    if (!window.confirm("Delete this reuse listing?")) return;
                    try {
                      await api.delete(`/reuse-listings/${id}`, { params: { user_id: user.id } });
                      toast.success("Listing deleted");
                      refresh();
                    } catch (err) {
                      toast.error(err?.response?.data?.detail || "Failed");
                    }
                  }} onMarkTaken={async (id) => {
                    try {
                      await api.patch(`/reuse-listings/${id}`, { status: "taken" }, { params: { user_id: user.id } });
                      toast.success("Marked as taken");
                      refresh();
                    } catch (err) {
                      toast.error(err?.response?.data?.detail || "Failed");
                    }
                  }} />
                ))}
              </div>
            )}
          </Section>
        )}
      </div>
    </Layout>
  );
}


function ReuseTile({ listing, onDelete, onMarkTaken }) {
  const navigate = useNavigate();
  const [reqCount, setReqCount] = useState(null);
  useEffect(() => {
    const userId = getUser()?.id;
    if (!userId) return;
    api.get(`/reuse-listings/${listing.id}/requests`, { params: { user_id: userId } })
      .then((r) => setReqCount(r.data.filter((x) => x.status === "pending").length))
      .catch(() => setReqCount(0));
  }, [listing.id]);
  const statusMap = {
    available: "bg-[#4A6B56] text-white",
    reserved: "bg-[#D67B54] text-white",
    taken: "bg-[#1B3624] text-white",
    hidden: "bg-red-100 text-red-700",
    expired: "bg-[#EBE8E0] text-[#6B6965]",
  };
  return (
    <Card
      data-testid={`reuse-tile-${listing.id}`}
      role="button"
      tabIndex={0}
      onClick={() => navigate(`/reuse/${listing.id}`)}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); navigate(`/reuse/${listing.id}`); } }}
      className="overflow-hidden rounded-2xl border-[#E2DFD8] p-0 cursor-pointer hover:border-[#4A6B56] transition focus:outline-none focus:ring-2 focus:ring-[#4A6B56]"
    >
      {listing.image_urls?.[0] && (
        <img src={listing.image_urls[0]} alt="" className="w-full h-36 object-cover" />
      )}
      <div className="p-5">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <Badge className={`${statusMap[listing.status] || statusMap.available} hover:opacity-90 uppercase text-[10px]`}>{listing.status}</Badge>
          {reqCount != null && reqCount > 0 && (
            <Badge data-testid={`reuse-tile-reqcount-${listing.id}`} className="bg-[#D67B54] text-white uppercase text-[10px]">
              {reqCount} pending
            </Badge>
          )}
        </div>
        <div className="mt-2 font-medium" style={{ fontFamily: "Outfit" }}>{listing.title}</div>
        <div className="text-xs text-[#6B6965] mt-1">{listing.category} · {listing.postcode_area}</div>
        <div className="mt-4 flex items-center justify-between gap-2">
          <span className="text-sm font-medium text-[#4A6B56]">View →</span>
          <div className="flex gap-1">
            {listing.status !== "taken" && (
              <Button
                data-testid={`reuse-tile-mark-taken-${listing.id}`}
                variant="ghost"
                size="icon"
                onClick={(e) => { e.stopPropagation(); onMarkTaken(listing.id); }}
                title="Mark as taken"
                className="text-[#6B6965] hover:text-[#4A6B56]"
              >
                <CheckCircle2 className="w-4 h-4" />
              </Button>
            )}
            <Button
              data-testid={`reuse-tile-delete-${listing.id}`}
              variant="ghost"
              size="icon"
              onClick={(e) => { e.stopPropagation(); onDelete(listing.id); }}
              title="Delete listing"
              className="text-[#6B6965] hover:text-red-600"
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>
    </Card>
  );
}

function StatCard({ icon, label, value, className, testId }) {  return (
    <Card data-testid={testId} className={`${className} p-6 rounded-2xl border-[#E2DFD8] bg-[#F6F4F0]`}>
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center text-[#4A6B56]">{icon}</div>
        <div>
          <div className="text-xs text-[#6B6965] uppercase tracking-wide">{label}</div>
          <div className="text-3xl font-medium mt-0.5" style={{ fontFamily: "Outfit" }}>{value}</div>
        </div>
      </div>
    </Card>
  );
}

function Section({ title, children, testId }) {
  return (
    <section className="mt-12 fade-up" data-testid={testId}>
      <h2 className="text-2xl tracking-tight font-medium mb-4" style={{ fontFamily: "Outfit" }}>
        {title}
      </h2>
      {children}
    </section>
  );
}

function Empty({ text, cta, to }) {
  const navigate = useNavigate();
  return (
    <Card className="p-10 rounded-2xl border-dashed border-[#E2DFD8] bg-transparent text-center">
      <div className="text-[#6B6965]">{text}</div>
      {cta && to && (
        <Button
          onClick={() => navigate(to)}
          className="mt-4 rounded-full bg-[#1B3624] hover:bg-[#274d33] text-white"
        >
          {cta}
        </Button>
      )}
    </Card>
  );
}

function BidStatus({ status }) {
  const map = {
    pending: "bg-[#EBE8E0] text-[#1B3624]",
    accepted: "bg-[#4A6B56] text-white",
    rejected: "bg-[#F6F4F0] text-[#6B6965]",
  };
  return <Badge className={`${map[status]} hover:${map[status]} uppercase text-[10px]`}>{status}</Badge>;
}

function PostStatus({ status }) {
  const map = {
    open: "bg-[#D67B54] text-white",
    accepted: "bg-[#4A6B56] text-white",
    completed: "bg-[#1B3624] text-white",
  };
  return <Badge className={`${map[status] || "bg-[#EBE8E0] text-[#1B3624]"} hover:opacity-90 uppercase text-[10px]`}>{status}</Badge>;
}

function EscrowPill({ status }) {
  const map = {
    held: "bg-[#D67B54]/15 text-[#D67B54]",
    released: "bg-[#4A6B56]/15 text-[#4A6B56]",
    disputed: "bg-red-100 text-red-700",
    refunded: "bg-[#EBE8E0] text-[#6B6965]",
  };
  return <Badge data-testid={`pill-${status}`} className={`${map[status] || "bg-[#EBE8E0] text-[#1B3624]"} hover:opacity-90 uppercase text-[10px]`}>{status || "—"}</Badge>;
}
